'use client';

import { useTranslations } from 'next-intl';
import {
  useIsLegacyTokenUser,
  useFreeTierLabel,
  useProfile,
} from '@/shared/api/hooks/use-user';
import type { Project } from '@/types/project';

/**
 * What the account-nav status chrome should render, post-pivot.
 *
 * - `tokens` — LEGACY users only: keep the live token number/badge.
 * - `builder_pass` / `facade_pack` — the user holds a paid entitlement
 *   (server-computed `access_badge`, Builder Pass > Facade Pack). Surfaces a
 *   premium crown + the English brand `label` (rendered verbatim). Takes
 *   precedence over the per-building pack/free chrome — a privileged account
 *   reads the same status everywhere, regardless of the building in view.
 * - `pack` — a non-legacy user viewing a building whose Facade Pack is active:
 *   "Facade Pack" status (album & estimate included) — no token number ever.
 * - `free` — a non-legacy free-tier user: the server-localized
 *   `free_tier.designs_label` ("5 free designs left").
 * - `none` — nothing to show (profile not seeded, or a non-legacy user off a
 *   pack building with no free-tier label).
 *
 * Pack users NEVER see token numerics; the legacy gate is the single
 * server-driven switch (P4.3).
 */
export type AccountChromeKind =
  | 'tokens'
  | 'builder_pass'
  | 'facade_pack'
  | 'pack'
  | 'free'
  | 'none';

export interface AccountChrome {
  kind: AccountChromeKind;
  /** Short label for `pack` / `free`; absent for `tokens` (uses the number). */
  label?: string;
  /**
   * Compact remaining-designs counter for the free tier, built from the numeric
   * `free_tier.designs_remaining`. Present only on `free` chrome when the number
   * is known and the tier is active — surfaces (e.g. the sidebar account chip)
   * prefer it over the long `label` to avoid truncation. The full server
   * `designs_label` stays in `label` for surfaces with room.
   */
  compactCounter?: string;
}

/**
 * Resolve the account-nav status chrome. `currentProject` is the building the
 * user is currently scoped to (the nav surfaces already resolve it from the
 * route) — used only to surface the "Facade Pack" status when its pack is
 * active. Legacy users always keep the token path regardless of building.
 */
export function useAccountChrome(
  currentProject?: Project | null
): AccountChrome {
  const tPacks = useTranslations('packs');
  const isLegacy = useIsLegacyTokenUser();
  const freeLabel = useFreeTierLabel();
  const profile = useProfile().data;
  const freeTier = profile?.free_tier;
  const accessBadge = profile?.access_badge;

  // Legacy accounts keep the token economy verbatim (numerics allowed).
  if (isLegacy === true) {
    return { kind: 'tokens' };
  }

  // Profile not seeded yet — don't flash anything.
  if (isLegacy === undefined) {
    return { kind: 'none' };
  }

  // Paid entitlement wins over per-building chrome: a Builder Pass / Facade
  // Pack holder reads the same crown + brand label everywhere (the server
  // already resolved precedence). The brand `label` is shown verbatim.
  if (accessBadge) {
    return { kind: accessBadge.kind, label: accessBadge.label };
  }

  // Non-legacy: a building with an active pack shows the Facade Pack status.
  if (currentProject?.attributes?.pack?.is_active) {
    return { kind: 'pack', label: tPacks('headerStatus') };
  }

  // Otherwise the free-tier counter (server-localized), when active.
  if (freeLabel) {
    // Compact remaining-designs counter for space-constrained surfaces — built
    // only when the number is known and the tier is active. It's a bare counter
    // (no i18n string needed); the long `label` stays as the fallback.
    const compactCounter =
      freeTier?.is_active && typeof freeTier.designs_remaining === 'number'
        ? String(freeTier.designs_remaining)
        : undefined;
    return { kind: 'free', label: freeLabel, compactCounter };
  }

  return { kind: 'none' };
}

/**
 * Server-resolved Account-tab nav glyph, with precedence ALREADY applied by the
 * API. This is the single source of truth for the bottom-nav / tab glyph: the
 * client reads `profile.account_chrome.kind` verbatim and never re-derives the
 * glyph by combining `tokens.is_legacy` + `free_tier.is_active` + `access_badge`
 * (API-driven-state-flags rule). It collapses the richer `useAccountChrome`
 * kinds — a paid Facade Kit / Builder Pass account reads `premium` even while a
 * free-tier counter is still active, so the crown is never suppressed by the "5".
 *
 * Display values for the number-bearing kinds are read from the SAME server
 * blocks the legacy derivation used (token balance for `tokens`, the compact
 * remaining-designs counter for `free`) — only the KIND decision moved server-side.
 */
export type AccountNavGlyphKind = 'premium' | 'tokens' | 'free' | 'none';

export interface AccountNavGlyph {
  kind: AccountNavGlyphKind;
  /** Compact remaining-designs counter for the `free` glyph (e.g. "5"). */
  compactCounter?: string;
}

export function useAccountNavGlyph(): AccountNavGlyph {
  const isLegacy = useIsLegacyTokenUser();
  const freeLabel = useFreeTierLabel();
  const profile = useProfile().data;
  const freeTier = profile?.free_tier;
  const serverKind = profile?.account_chrome?.kind;

  // Compact free-tier counter (built only when the tier is active and the number
  // is known) — surfaced in the glyph slot for the `free` kind.
  const compactCounter =
    freeTier?.is_active && typeof freeTier.designs_remaining === 'number'
      ? String(freeTier.designs_remaining)
      : undefined;

  // Server enum is the source of truth (precedence already applied: premium
  // outranks the free counter).
  if (serverKind) {
    return serverKind === 'free'
      ? { kind: 'free', compactCounter }
      : { kind: serverKind };
  }

  // Defensive fallback for stale caches predating `account_chrome` — corrected
  // so premium/legacy outrank the free counter (the bug being fixed).
  if (isLegacy === true) {
    return { kind: 'tokens' };
  }
  if (isLegacy === undefined) {
    return { kind: 'none' };
  }
  if (profile?.access_badge) {
    return { kind: 'premium' };
  }
  if (freeLabel) {
    return { kind: 'free', compactCounter };
  }
  return { kind: 'none' };
}
